import Head from "next/head";
import EmailSignupBanner from "../components/EmailSignupBanner";
import Layout from "../components/Layout";
import { Button, Center, Loader, Modal, Popover, Progress, Space, Switch, Text} from '@mantine/core'
import { MenuId } from "../components/Header";
import styles from './user-images.module.scss'


import { GetServerSideProps } from "next";
import { getJWTTokenFromUser, getUserInformation } from "../lib/data";
import { DateTime } from "luxon";
import { RusAuthProps, RusContextProps, UserImage, UserImageSearchInformation, UserImageSearchResult } from "../lib/definitions";

import { AspectBox } from "../components/AspectBox";
import { ChangeEvent, createRef, useEffect, useLayoutEffect, useRef, useState, JSX} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faTrash, faArrowsRotate } from "@fortawesome/pro-regular-svg-icons";
import {faPlus, faHeart as faSolidHeart} from '@fortawesome/pro-solid-svg-icons';

import useAuth from "../lib/useAuth";
import { okModal } from "../lib/utils";

import { useWindowEvent } from '@mantine/hooks';
import Editable from "../components/Editable";
import Link from "next/link";
import { useRouter } from "next/router";


import { modals } from "@mantine/modals";
import { ImageUpload, ImageUploadHandle, ImageUploadedProps } from "../components/ImageUpload";
import Avatar from "../components/Avatar";
import { useAuth as useClerkAuth } from '@clerk/nextjs'
import { getClerkHeader } from "../lib/common";
import { getAuth } from '@clerk/nextjs/server';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
  
type Data = {
  userImages : UserImageSearchResult
  }

const pageSize = 100;
export default function UserImages({data} : {data: Data & RusAuthProps & RusContextProps})
{
  let [lightboxImage, setLightboxImage] = useState<UserImage | null>(null);
  let [showLightboxFlag, setShowLightboxFlag] = useState(false);
  let [images, setImages] = useState(data.userImages.images);
  const {userInformation, authToken} = useAuth();
  const {isSignedIn} = useClerkAuth();

  let uploadRef = createRef<ImageUploadHandle>();

  let canAdd = userInformation?.subscribed;

  let [imageWidth, setImageWidth] = useState(540);
  let [imageHeight, setImageHeight] = useState(540);
  let [noMoreImages, setNoMoreImages] = useState(false);
  let [loading, setLoading] = useState(false);

  const [justMe, pSetJustMe] = useState(false);
  const { getToken } = useClerkAuth();

  let router = useRouter();
  
  let offset = useRef(0);

  function resizeImage()
  {
    if (lightboxImage)
    {
      let maxWidth = Math.max(window.innerWidth - 50,200);
      let maxHeight = Math.max(window.innerHeight - 100, 200);
      let width = maxWidth;
      let height = width * lightboxImage.height/lightboxImage.width;

      if (height>maxHeight)
      {
        height = maxHeight;
        width = height * lightboxImage.width / lightboxImage.height;
      }

      setImageWidth(width);
      setImageHeight(height);
      
    }
  }
  useIsomorphicLayoutEffect(()=>
  {
    resizeImage();
  }
  ,[lightboxImage]);

  useWindowEvent("resize", ()=>{resizeImage()});

  function showLightbox(image: UserImage)
  {
    setLightboxImage(image);
    setShowLightboxFlag(true);
  }

  function closeLightbox()
  {
    setShowLightboxFlag(false);
  }

  async function uploadImage()
  {
    if (!canAdd)
    {
      okModal("To upload your own images, please log on or subscribe");
      return;
    }

    if (uploadRef?.current)
    {
      uploadRef.current.show();
    }

  }

  async function setJustMe(value: boolean)
  {
    setLoading(true);

    const clerkToken = await getToken();

    pSetJustMe(value);
    var x = await getUserImages({offset:0, count:pageSize, userId: value?((userInformation?.user_id)??0):0}, authToken, clerkToken);
    setImages(x.images);
    offset.current=0;


    setNoMoreImages(x.images.length==0);
    

    setLoading(false);

  }

  async function deleteImage(image:UserImage)
  {
  
    const clerkToken = await getToken();
    modals.openConfirmModal(
      {
        title:"Delete image",
        children: (<Text>Are you sure you want to delete this image? This cannot be undone.</Text>),
        labels:{confirm:'Confirm', cancel:'Cancel'},
        onConfirm:async () =>
        {
          const index = images.indexOf(image);
          //console.log(index);
          if (index==-1)
          {
            return;
          }
      
          let newImages = [...images];
          newImages.splice(index,1);
          setImages(newImages);

          await fetch(process.env.NEXT_PUBLIC_ELTON_ROUTE + `/userimages/${image.id}`, 
     
     {
      method: 'DELETE',         
    headers: {... authToken?{'auth-token': authToken}:{}, ...getClerkHeader(clerkToken)} as HeadersInit
      
    });
      
        }

      }
    )

  }
  async function replaceImage(image:UserImage)
  {

  }


  async function likeImage(image:UserImage)
  {

    const clerkToken = await getToken();

    if (!userInformation?.subscribed)
    {
      okModal("To like images, please log on or subscribe");
      return;
    }
    let newImages = [...images];
    let newImage = newImages.find(x=>x.id==image.id);

    if (!newImage)
    {
      return;
    }
    newImage.hasLiked=!image.hasLiked;

    if (newImage.hasLiked)
    {
      newImage.likeCount++;
    }
    else
    {
      newImage.likeCount--;
    }
    setImages(newImages);    

     await fetch(process.env.NEXT_PUBLIC_ELTON_ROUTE + `/userimages/${newImage.id}/like`, 
     
     {
      method: 'POST',         
      headers: {
        'Content-Type': 'application/json',
         ... authToken?{'auth-token': authToken}:{},
          ...getClerkHeader(clerkToken)
      } as HeadersInit,
      body:  JSON.stringify({like:newImage.hasLiked}),
    });

  }

  async function loadMore()
  {
    setLoading(true);
    const clerkToken = await getToken();

    offset.current = offset.current + pageSize;
    let nextImages = await getUserImages({offset:offset.current, count:pageSize,  userId: justMe?((userInformation?.user_id)??0):0}, authToken, clerkToken);
    let newImages = [... images, ...nextImages.images];
    setImages(newImages);

    if (nextImages.images.length==0)
    {
      setNoMoreImages(true);
    }

    setLoading(false);

  }

  async function saveTitle(x: string)
  {
    if (!lightboxImage)
    {
      return;
    }

    let newImages = [...images];
    let newImage = newImages.find(x=>x.id==lightboxImage?.id);

    if (!newImage)
    {
      return;
    }

    newImage.description = x;
    setLightboxImage(newImage);
    setImages(newImages);

    const clerkToken = await getToken();

    await fetch(process.env.NEXT_PUBLIC_ELTON_ROUTE + `/userimages/${newImage.id}/description`, 
     
    {
     method: 'POST',         
     headers: {
       'Content-Type': 'application/json',
        ... authToken?{'auth-token': authToken}:{}, 
        ...getClerkHeader(clerkToken)
     } as HeadersInit,
     body:  JSON.stringify({description:newImage.description}),
   });

  }
  
  function imageUploaded(args: ImageUploadedProps)
  {
    let image = args.data as UserImage;
    //console.log(image);
    let newImages = [image, ...images];
    setImages(newImages);
  }

  let logonUrl="/log-in?redirect_to=" + router.asPath;

  let url = `https://user-images.rawumberstudios.com/${lightboxImage?.guid}_1080.jpg`;

  let editable = (userInformation?.is_admin || (userInformation?.user_id==lightboxImage?.user.id && isSignedIn)) ;

 
    return(
        <>
        <Modal opened={showLightboxFlag} onClose={closeLightbox} fullScreen closeOnEscape={false}>

          <Editable centre readonly={!editable} value={lightboxImage?.description??''} prompt="Click here to give your image a title" placeholder="" onSave={saveTitle}/>

      <div style={{display:"flex", width:"100%", height:"100%", justifyContent:"center"}}>
      <div className={styles.lightboxBorder} style={{width:imageWidth, height:imageHeight}}>
        {lightboxImage && <div style={{ width:"100%", height:"100%", backgroundImage:`url('${url}')`, backgroundSize:"contain", backgroundPosition:"center", backgroundRepeat:"no-repeat"}}></div>}
        </div>
        </div>

        
        </Modal>


           
                        <ImageUpload ref={uploadRef} showTitle={true} postRoute={process.env.NEXT_PUBLIC_ELTON_ROUTE+'/userimages/upload'} onUploaded={imageUploaded} />



      
        <Layout menuId={MenuId.online}>

  
  <Head>
      <title>Our subscribers&rsquo; most recent work</title>
      <meta name="description" content="Work our subscribers have created from our video livestreams, and video and image libraries"/>      
  </Head>
  <div className = {styles.mainContent}>
  <h1 className={styles.h1}>Our subscribers&rsquo; most recent work</h1>
  {/*<JWHostedPlayer jwId='QpsHwmpS' autostart={true} controls={false} loop={true} mute={true}/>*/}
    <Text ta="center">Work our subscribers have created from our video livestreams, and video and image libraries.
    {canAdd?<> If you&rsquo;re a subscriber, please upload your artwork or tag it with #rawumberlive on Instagram</>:<> Please <Link href={logonUrl}>log on</Link> or <Link href='/subscription'>subscribe</Link> to upload your work, or tag it with #rawumberlive on Instagram.</>}</Text>
    {canAdd && <><Space h='md'/><Center><Switch checked={justMe} onChange={async (event) => await setJustMe(event.currentTarget.checked)} size="md" label="Just show my own images"></Switch></Center><Space h='md'/></>}
  </div>
  
<div style={{maxWidth:1000, marginLeft:"auto", marginRight:"auto", position:"relative"}}>
<div onClick={async () => await uploadImage()} className={styles.hover} style={{position:"sticky",  margin:5, borderRadius:5, display:"flex", justifyContent:"center", alignItems:"center", backgroundColor:"#d1cebc"}}>
  <div  style={{backgroundColor:"#99957f", borderRadius:"50%", width:64, height:64, display:"flex", justifyContent:"center", alignItems:"center", left:128, top:10}}><FontAwesomeIcon icon={faPlus} fontSize={48}/></div>
  </div>
<div className={styles.list}>
      {images.map(x=> <UserImageDisplay key={x.id} image={x} showLightbox={showLightbox} likeImage={likeImage} deleteImage={deleteImage} replaceImage={replaceImage}/>)}
</div>

{!noMoreImages && <Button disabled={loading} variant="light" fullWidth onClick={async () => {await loadMore();}}>{loading?<Loader/>:<>Load more ...</>}</Button>}

</div>
  <EmailSignupBanner/>
  </Layout>



        </>

    );
  }


function UserImageDisplay(props: {image: UserImage, 
  showLightbox: (x:UserImage)=>void,
  likeImage: (x: UserImage)=>Promise<void>,
  deleteImage: (x:UserImage)=>Promise<void>,
  replaceImage: (x:UserImage)=>Promise<void>
}
  
  )
{

  const {userInformation}=useAuth();
  const {isSignedIn} = useClerkAuth();
  
  let [hover, setHover] = useState(false);
  let canEdit = (userInformation?.is_admin || (userInformation?.user_id== props.image.user.id && isSignedIn));

  async function mouseEnter(e: React.MouseEvent)
  {
    setHover(true);
  }

  function onClick(e: React.MouseEvent)
  {
    props.showLightbox(props.image);
  }

  async function mouseLeave(e: React.MouseEvent)
  {
    setHover(false);
  }

  let name = props.image.user?.userName || props.image.instagram?.userName;

  if (!name)
  {
    name="Instagram user";
  }

  let popText : JSX.Element | undefined;

  if (!props.image.user!.userName )
  {
    popText = props.image.instagram?.userName?<>You can see the post on Instagram <a href={props.image.instagram?.permalink} target="_blank">here</a></>:<>We&rsquo;re not sure who this is, but you can <a href={props.image.instagram?.permalink} target="_blank">see the post on Instagram</a>.</>
  }
  //console.log(props.image);

    return <div className={styles.image} >
     
        <AspectBox width={1} height={1} >
            {/* eslint-disable-next-line @next/next/no-img-element*/}

            <div onMouseEnter={mouseEnter} onMouseLeave={mouseLeave}>
            <img  src={`https://user-images.rawumberstudios.com/${props.image.guid}_240.jpg`} alt={`Photo by ${name}`} onClick={onClick}/>
            {(hover && canEdit) &&  <div className={styles.hoverImage}><FontAwesomeIcon className={styles.hoverInline} icon={faTrash} onClick={async () => await props.deleteImage(props.image)}/>{false && <FontAwesomeIcon onClick={async () => await props.replaceImage(props.image)} style={{marginLeft:10}} className={styles.hoverInline} icon={faArrowsRotate}/>}</div>}

            </div>
        </AspectBox>
        
        <div style={{display:"flex",marginTop:5,marginBottom:6}}><Avatar userId={props.image.user.id} name={name} useInstagramIcon/>

        {!popText && <div style={{fontSize:14, fontWeight:"bold", paddingBottom:6}}>{name}</div>}
        {popText &&
       
      
        <Popover>
          <Popover.Target>
        <a style={{fontSize:14, fontWeight:"bold", paddingBottom:6, textDecorationLine:"underline", textDecorationStyle:"dotted", cursor:"pointer"}}>{name}</a>        
        </Popover.Target>
        <Popover.Dropdown>
          <div style={{maxWidth:300}}>{popText}</div>
        </Popover.Dropdown>
        </Popover>
      }

        {/* need to be an a tag for iOS to work*/}
        <a href='#' className={styles.hoverInline} onClick={async (e)=>{e.preventDefault(); await props.likeImage(props.image);}} style={{marginLeft:"auto"}}><FontAwesomeIcon  color={props.image.hasLiked?"red":"black"}  icon={props.image.hasLiked?faSolidHeart:faHeart} /><span style={{marginLeft:2, marginRight:5}}>{props.image.likeCount}</span></a>
        

        {/*<div className={styles.hoverInline}><FontAwesomeIcon icon={faComment} /><span style={{marginLeft:2, marginRight:5}}>{props.image.commentCount}</span></div>*/}
        </div>
        
    </div>
}


async function getUserImages(search: UserImageSearchInformation, token?: string, clerkToken?: string | null)
{
    let json = encodeURIComponent(JSON.stringify(search, (_, v) => { if (v != null) return v; }));
    let options = { 
        method: 'GET',
        cache: "no-store" as RequestCache,
         headers: {
          ... token?{'auth-token': token}:{}, 
          ...getClerkHeader(clerkToken)} as HeadersInit

        };


    let ret : UserImageSearchResult =  await (await fetch(process.env.NEXT_PUBLIC_ELTON_ROUTE + "/userimages?json=" + json, options)).json();

    return ret;
        
}
export const getServerSideProps : GetServerSideProps = async (context)=>
{

  let user = await getUserInformation(context.req);
   
    let token;
    
    if (user.subscribed)
    {
        token = await getJWTTokenFromUser(user);
    }

    const { getToken } = await getAuth(context.req);

    const clerkToken = await getToken();

    let list = await getUserImages({offset:0, count:pageSize, userId:0}, token, clerkToken);

    return {
      props: {
        data: 
        { 
            userImages: list,
            dateProcessed: DateTime.now().toISO()
        }
     }   

    }
}
